import { useState, useEffect } from "react";
import {
  fetchAuthenticatedImage,
  revokeImageUrl,
} from "../util/authenticatedMedia";
import { isProxyUrl } from "../util/imageUtils";

/**
 * Hook to load an authenticated image
 * @param {string} imageUrl - The proxy URL for the image
 * @param {string} fallbackUrl - Optional fallback image URL
 * @returns {{ src: string, loading: boolean, error: Error | null }}
 */
export function useAuthenticatedImage(imageUrl, fallbackUrl = null) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let blobUrl = null;

    async function loadImage() {
      if (!imageUrl) {
        setSrc(fallbackUrl);
        return;
      }

      // Direct/public URLs should render as-is and not be fetched with auth.
      if (!isProxyUrl(imageUrl)) {
        setSrc(imageUrl);
        return;
      }

      setSrc(null);
      setLoading(true);
      setError(null);

      try {
        blobUrl = await fetchAuthenticatedImage(imageUrl);
        if (mounted) {
          if (blobUrl) {
            setSrc(blobUrl);
          } else {
            setSrc(fallbackUrl);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err);
          setSrc(fallbackUrl);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadImage();

    // Cleanup: revoke blob URL when component unmounts or URL changes
    return () => {
      mounted = false;
      if (blobUrl) {
        revokeImageUrl(blobUrl);
      }
    };
  }, [imageUrl, fallbackUrl]);

  return { src, loading, error };
}

export default useAuthenticatedImage;

